import type { PropsWithChildren, ReactNode } from 'react';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { bootstrap } from '@/features/auth/authThunks';
import type { RootState } from '@/app/store';
import SplashScreen from '@/components/common/SplashScreen';
import { useMinSplash } from '@/hooks/useMinSplash';

type Props = PropsWithChildren<{ unauthenticatedFallback?: ReactNode }>;

export default function RequireAuth({ children, unauthenticatedFallback }: Readonly<Props>) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { user, bootstrapped, loading } = useAppSelector((s: RootState) => s.auth);
  const {
    createLoading,
    workspacesList,
    loading: workspaceLoading,
  } = useAppSelector((s: RootState) => s.workspace);
  const hasWorkspace = Array.isArray(workspacesList) && workspacesList.length > 0;
  const workspaceListNotYetLoaded = !!user && workspacesList === null && workspaceLoading;

  useEffect(() => {
    if (!bootstrapped && !loading) {
      dispatch(bootstrap());
    }
  }, [bootstrapped, loading, dispatch]);

  const pending = !bootstrapped || loading || createLoading || workspaceListNotYetLoaded;

  const showSplash = useMinSplash(pending, 600);

  if (showSplash) {
    return <SplashScreen />;
  }

  if (!user) {
    // Certaines destinations ne se gagnent pas en créant un compte : les y envoyer ferait naître
    // des comptes inutiles au lieu de dire que l'accès est réservé.
    if (unauthenticatedFallback !== undefined) {
      return <>{unauthenticatedFallback}</>;
    }
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (hasWorkspace && !user.ready) {
    if (location.pathname === '/user-information') {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  }

  if (
    user &&
    !user.ready &&
    !hasWorkspace &&
    (!user.firstName || !user.lastName || !user.phoneNumber) &&
    location.pathname !== '/user-information'
  ) {
    return <Navigate to="/user-information" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
